<template>
  <div style="position: absolute; left: -9999px; top: -9999px">
    <div
      ref="pdfWrapper"
      class="pdf-container bg-white"
      style="
        width: 800px;
        padding: 40px;
        color: black;
        font-family: 'Helvetica', 'Arial', sans-serif;
        box-sizing: border-box;
        position: relative;
        margin: 0;
      "
    >
      <!-- Header -->
      <div
        style="
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
        "
      >
        <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 6px">
          <div v-if="orgLogoUrl" style="height: 60px; max-width: 180px; overflow: hidden">
            <img
              :src="orgLogoUrl"
              alt="Logo"
              style="height: 100%; width: auto; object-fit: contain"
              crossorigin="anonymous"
            />
          </div>
          <div class="text-sm" style="line-height: 1.5">
            <strong v-if="!orgLogoUrl" style="font-size: 15px">{{ orgData.name || 'Zavron Finance Pvt Ltd' }}</strong><br v-if="!orgLogoUrl" />
            <span v-if="orgAddressLine1">{{ orgAddressLine1 }}<br /></span>
            {{ orgAddressCity }}, {{ orgAddressState }}<br />
            <span v-if="orgData.email">{{ orgData.email }}<br /></span>
            {{ orgData.phone || '0712-2520007' }}
          </div>
        </div>
        <div style="text-align: right">
          <div
            class="font-bold text-blue-900"
            style="
              font-family: serif;
              color: #1e3a8a;
              letter-spacing: -1px;
              font-size: 24px;
              line-height: 1;
              text-transform: uppercase;
            "
          >
            {{ orgNameParts[0] }}
          </div>
          <div
            class="text-center tracking-widest text-blue-800"
            style="color: #1e40af; margin-top: 2px; font-size: 10px; text-transform: uppercase"
          >
            {{ orgNameParts[1] }}
          </div>
        </div>
      </div>

      <!-- Title -->
      <h2
        style="
          text-align: center;
          font-size: 1.25rem;
          font-weight: bold;
          margin-bottom: 1.5rem;
          font-style: italic;
          text-decoration: underline;
        "
      >
        Foreclosure Letter
      </h2>

      <!-- Date -->
      <div style="text-align: right; font-size: 0.875rem; margin-bottom: 1.5rem">
        <em>Date: {{ formattedDate }}</em>
      </div>

      <!-- To Section -->
      <div class="text-sm italic mb-6" style="margin-bottom: 1.5rem; line-height: 1.5">
        <div>To,</div>
        <div>{{ customerName }}</div>
        <div>S/O: {{ fatherName }}, {{ customerAddress }}</div>
      </div>

      <!-- Subject -->
      <div class="text-sm mb-6">
        <span class="font-bold italic" style="text-decoration: underline">Subject:</span>
        <span class="italic"
          >Foreclosure of Finance for Loan A/c Number:- {{ loanDetails.caseNo || '' }}</span
        >
      </div>

      <!-- Payment Type Info Box -->
      <div
        style="
          margin-bottom: 1.5rem;
          border: 1px solid #1e3a8a;
          border-radius: 4px;
          overflow: hidden;
        "
      >
        <div
          style="
            background-color: #1e3a8a;
            color: white;
            padding: 8px 14px;
            font-weight: bold;
            font-size: 0.875rem;
            letter-spacing: 0.5px;
          "
        >
          PAYMENT DETAILS
        </div>
        <div style="padding: 12px 14px; font-size: 0.85rem; line-height: 1.7">
          <div>
            <strong>Payment Type:</strong>
            {{ paymentTypeLabel }}
          </div>
          <div v-if="paymentSummary.closureType === 'prepayment' && !paymentSummary.isFullPayment">
            <strong>Prepayment Mode:</strong> {{ prepaymentModeLabel }}
          </div>
          <div v-if="paymentSummary.amountPaid">
            <strong>Amount Paid:</strong> ₹{{ formatCurrencyPdf(paymentSummary.amountPaid) }}
          </div>

          <!-- Tenure Change Info -->
          <template v-if="showTenureChange">
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb">
              <strong>Tenure Update:</strong>
            </div>
            <div style="padding-left: 12px">
              <div>Previous Remaining Tenure: <strong>{{ paymentSummary.originalTenure }} months</strong></div>
              <div>New Remaining Tenure: <strong>{{ paymentSummary.newTenure }} months</strong></div>
              <div>EMIs Reduced by: <strong>{{ paymentSummary.tenureReduction }} months</strong></div>
            </div>
          </template>

          <!-- EMI Amount Change Info -->
          <template v-if="showEmiChange">
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb">
              <strong>EMI Amount Update:</strong>
            </div>
            <div style="padding-left: 12px">
              <div>Previous EMI Amount: <strong>₹{{ formatCurrencyPdf(paymentSummary.originalEmi) }}</strong></div>
              <div>New EMI Amount: <strong>₹{{ formatCurrencyPdf(paymentSummary.newEmi) }}</strong></div>
            </div>
          </template>
        </div>
      </div>

      <!-- Letter Body -->
      <div class="text-sm italic mb-4" style="margin-bottom: 1rem">
        <p style="margin-bottom: 1rem">Dear Sir/Madam,</p>
        <p style="line-height: 1.5; margin-bottom: 1rem">
          We value your relationship with {{ orgData.name || 'Zavron Finance Pvt Ltd' }}. In
          response to your request for Prepayment of your above mentioned loan, we give you the
          details of the amount payable as on {{ formattedDate }} :-
        </p>
      </div>

      <!-- Table -->
      <table
        class="w-full text-sm mb-6"
        style="
          border-collapse: collapse;
          border: 1px solid black;
          text-align: left;
          table-layout: fixed;
          width: 100%;
        "
      >
        <thead>
          <tr>
            <th class="font-bold" style="border: 1px solid black; width: 70%">
              <div style="padding: 10px; box-sizing: border-box">PARTICULARS</div>
            </th>
            <th class="font-bold" style="border: 1px solid black; width: 30%">
              <div style="padding: 10px; box-sizing: border-box">AMOUNT (RS)</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">Principal Outstanding</div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">
                {{ formatCurrencyPdf(foreclosureData.principalOutstanding) }}
              </div>
            </td>
          </tr>
          <tr>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">Interest Outstanding</div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">
                {{ formatCurrencyPdf(interestOutstanding) }}
              </div>
            </td>
          </tr>
          <tr>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">Broken Interest</div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">0.00</div>
            </td>
          </tr>
          <tr v-for="(charge, index) in penaltyCharges" :key="'penalty_' + index">
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">
                {{ charge.chargeType || 'Penalty Charge' }}
                <span
                  v-if="charge.chargeType && charge.chargeType.toLowerCase().includes('prepayment')"
                >
                  @{{ loanDetails.prepaymentCharges || 0 }}% on outstanding amount
                </span>
              </div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">
                {{ formatCurrencyPdf(charge.amount) }}
              </div>
            </td>
          </tr>
          <tr v-if="lpcAmount > 0">
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">LPC (Late Payment Charges)</div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">{{ formatCurrencyPdf(lpcAmount) }}</div>
            </td>
          </tr>
          <tr v-if="handloanAmount > 0">
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">Handloan</div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">{{ formatCurrencyPdf(handloanAmount) }}</div>
            </td>
          </tr>
          <tr v-if="handloanLpcAmount > 0">
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">Handloan LPC</div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">{{ formatCurrencyPdf(handloanLpcAmount) }}</div>
            </td>
          </tr>
          <tr v-for="(charge, index) in dynamicChargesDetails" :key="'dc_' + index">
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">{{ charge.name }}</div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">{{ formatCurrencyPdf(charge.balance) }}</div>
            </td>
          </tr>
          <tr v-for="(discount, index) in discountWaivers" :key="'discount_' + index">
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">
                {{ discount.discountType || 'Discount/Waiver' }}
              </div>
            </td>
            <td style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">
                -{{ formatCurrencyPdf(discount.amount) }}
              </div>
            </td>
          </tr>
          <tr>
            <td class="font-bold" style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">Total Amount Payable</div>
            </td>
            <td class="font-bold" style="border: 1px solid black">
              <div style="padding: 10px; box-sizing: border-box">
                {{ formatCurrencyPdf(finalAmount) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Footer Note & Terms -->
      <div class="text-sm italic mb-4" style="line-height: 1.5">
        <p class="font-bold mb-1">
          Kindly issue a cheque in the name of {{ orgData.name || 'Zavron Finance pvt ltd' }} .
        </p>
        <p class="font-bold mb-1">
          For NEFT/RTGS/IMPS, transfer can be made at the below mentioned details:-
        </p>
        <p class="mb-0">Beneficiary Name:- {{ orgData.name || 'Zavron Finance pvt. ltd.' }}</p>
        <p class="mb-0">
          Account Number:-
          {{ loanDetails.caseNo ? loanDetails.caseNo : 'ZFPLTDIL145000372789628108831' }}
        </p>
        <p class="mb-1">IFSC:- {{ orgData.ifscCode || 'ICIC0000106' }}</p>
        <p class="mb-4">
          The full and final settlement will be accorded subject to clearance of cheque/DD and other
          charges as due on date.
        </p>

        <p class="font-bold mb-2">Kindly Note The following conditions</p>
        <ul style="padding-left: 20px; list-style-type: disc; margin-bottom: 0">
          <li style="margin-bottom: 4px">
            Above calculation assume that your last installment has been cleared.
          </li>
          <li style="margin-bottom: 4px">
            We have taken the date of Prepayment as on {{ formattedDate }}. For each day beyond
            {{ formattedDate }} an additional interest will be charged at the rate of Amount Dues
            per day.In Case of cheque return additional charges will be levied as per company
            policy.
          </li>
          <li style="margin-bottom: 4px">
            If you prepay the loan after, the installment for the following month will become
            payable.
          </li>
          <li style="margin-bottom: 4px">
            If you prepay the loan after Current Date of the month, request you to please make a
            stop payment of your next installment.
          </li>
          <li style="margin-bottom: 4px">
            The above mentioned amount is valid subject to clearance of all the cheques/installments
            till date.
          </li>
          <li style="margin-bottom: 4px">
            Prepayment charge is lower of the Pre-payment charge or interest outstanding for the
            unexpired period of the facility.
          </li>
          <li style="margin-bottom: 4px">
            In the event the Applicants wishes to procure the Postdated cheques and/ or security
            Postdated cheques that remain unbanked at the end of the loan tenure / change of
            repayment mode or details of such unbanked Postdated cheques and /or security Postdated
            cheques , the applicants must make a request for the same ,within 30 days of closure of
            loan/ change of repayment mode ,failing which Company Name shall have the discretion
            /responsibility to destroy the unbanked Postdated cheques including security Postdated
            cheques without any further notice and Company Name shall not have the obligation to
            return the same to the applicant.<br />Please note that the processing of such requests
            would entail charges as may be decided by Company Name from time to time.
          </li>
          <li>
            If you decide to Prepay, please make payments of the above " Total amount Payable"
            through a cheque or draft favoring {{ orgData.name || 'Company Name' }}.
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ForeclosurePdfTemplate',
  props: {
    orgData: { type: Object, default: () => ({}) },
    closureDate: { type: String, default: '' },
    customerName: { type: String, default: 'Customer Name' },
    fatherName: { type: String, default: 'Not available' },
    customerAddress: { type: String, default: 'Not available' },
    loanDetails: { type: Object, default: () => ({}) },
    foreclosureData: { type: Object, default: () => ({}) },
    penaltyCharges: { type: Array, default: () => [] },
    discountWaivers: { type: Array, default: () => [] },
    finalAmount: { type: Number, default: 0 },
    interestOutstanding: { type: Number, default: 0 },
    lpcAmount: { type: Number, default: 0 },
    handloanAmount: { type: Number, default: 0 },
    handloanLpcAmount: { type: Number, default: 0 },
    dynamicChargesDetails: { type: Array, default: () => [] },
    paymentSummary: {
      type: Object,
      default: () => ({
        closureType: 'foreclosure',
        isFullPayment: true,
        prepaymentApplication: '',
        amountPaid: 0,
        originalEmi: 0,
        newEmi: 0,
        originalTenure: 0,
        newTenure: 0,
        tenureReduction: 0
      })
    }
  },
  computed: {
    formattedDate() {
      if (!this.closureDate) return ''
      return new Date(this.closureDate)
        .toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
        .replace(/\//g, '-')
    },
    orgAddressLine1() {
      return this.orgData?.organization_address?.address_line_1 || ''
    },
    orgAddressCity() {
      return this.orgData?.organization_address?.city || 'Nagpur'
    },
    orgAddressState() {
      return this.orgData?.organization_address?.state || 'Maharashtra'
    },
    orgNameParts() {
      const name = this.orgData?.name || 'Zavron Finserv'
      const parts = name.split(' ')
      if (parts.length > 1) {
        return [parts[0], parts.slice(1).join(' ')]
      }
      return [name, '']
    },
    orgLogoUrl() {
      if (!this.orgData?.logo) return null
      let url = ''
      if (
        this.orgData.logo.startsWith('http://') ||
        this.orgData.logo.startsWith('https://') ||
        this.orgData.logo.startsWith('data:')
      ) {
        url = this.orgData.logo
      } else {
        url = `${process.env.VUE_APP_S3_BASE_URL || 'https://finfi-uat.s3.ap-south-1.amazonaws.com/'}${this.orgData.logo}`
      }
      const baseUrl = this.$axios?.defaults?.baseURL || 'https://uatapi.myfinfi.com'
      return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`
    },
    paymentTypeLabel() {
      if (this.paymentSummary.closureType === 'foreclosure' || this.paymentSummary.isFullPayment) {
        return 'Full Foreclosure (Loan Closure)'
      }
      return 'Part Payment (Prepayment)'
    },
    prepaymentModeLabel() {
      const mode = this.paymentSummary.prepaymentApplication
      if (mode === 'reduce_tenure') return 'Tenure Reduction'
      if (mode === 'reduce_emi') return 'EMI Amount Reduction'
      if (mode === 'custom_tenure') return 'Custom Tenure Adjustment'
      return 'Tenure Reduction'
    },
    showTenureChange() {
      const s = this.paymentSummary
      if (s.closureType === 'foreclosure' && !s.isFullPayment) return false
      const mode = s.prepaymentApplication
      return (
        s.closureType === 'prepayment' &&
        !s.isFullPayment &&
        (mode === 'reduce_tenure' || mode === 'custom_tenure') &&
        s.tenureReduction > 0
      )
    },
    showEmiChange() {
      const s = this.paymentSummary
      return (
        s.closureType === 'prepayment' &&
        !s.isFullPayment &&
        s.prepaymentApplication === 'reduce_emi' &&
        s.newEmi > 0 &&
        s.newEmi !== s.originalEmi
      )
    }
  },
  methods: {
    formatCurrencyPdf(value) {
      const val = Number(value) || 0
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val)
    },
    getHtmlElement() {
      return this.$refs.pdfWrapper
    }
  }
}
</script>
